export const DEFAULT_FILTER_ID = 1;

export const groupByRelativeDay = (events) =>
  events.reduce((groups, event) => {
    const last = groups[groups.length - 1];
    if (last?.key === event.relative_day) last.events.push(event);
    else groups.push({ key: event.relative_day, events: [event] });
    return groups;
  }, []);

// The "Previous" title only makes sense next to a today or yesterday group.
export const hasRecentGroups = (groups) =>
  groups.some(({ key }) => key === 'today' || key === 'yesterday');

const SCREEN_HINT = /\s*\[[^\]]*\]\s*$/;
const CONTROL_LISTING_SECTIONS = ['properties', 'developments'];

const toUserPermissionLabel = (subject, label = '') =>
  `${subject} ${label
    .replace(SCREEN_HINT, '')
    .replace(/^puede\(n\)/, 'puede')}`;

// Merges the panel's direct states (codename -> state) into the catalog it
// ships along (labels, order, dependencies) in the shape `PermissionsList`
// renders.
// Permissions the panel left out (gated by an inactive waffle flag) or the
// catalog doesn't know (the computed property importer) are not shown.
export const adaptUserPermissions = ({ panel, subject }) => {
  const {
    perms = {},
    control_listing_edition_active: controlListingActive = false,
    perms_hidden_under_control_listing_edition_active: hiddenCodenames = [],
    permission_catalog: catalog = {},
  } = panel ?? {};
  const sections = Object.values(catalog);

  const codenameById = new Map();
  sections.forEach((section) =>
    section.permissions.forEach(({ id, codename }) =>
      codenameById.set(id, codename)
    )
  );

  const enabledByCodename = Object.assign({}, ...Object.values(perms));
  const isShown = ({ codename }) =>
    codename in enabledByCodename &&
    !(controlListingActive && hiddenCodenames.includes(codename));

  return sections
    .map((section) => ({
      id: section.section_key,
      name: section.section_label,
      affectedByControlListing:
        controlListingActive &&
        CONTROL_LISTING_SECTIONS.includes(section.section_key),
      permissions: section.permissions.filter(isShown).map((permission) => ({
        id: permission.codename,
        label: toUserPermissionLabel(subject, permission.label),
        description: permission.massive_edit_help || null,
        enabled: enabledByCodename[permission.codename],
        disabled: false,
        ...(permission.parent_permission_id
          ? { depends_on: codenameById.get(permission.parent_permission_id) }
          : {}),
      })),
    }))
    .filter((section) => section.permissions.length > 0);
};

// The whole catalog in the shape `collectPermissionAndDependents` walks, hidden
// permissions included: one hidden by control listing still links its
// dependents to its own parent.
export const getPermissionDependencies = (panel) => {
  const sections = Object.values(panel?.permission_catalog ?? {});
  const codenameById = new Map(
    sections.flatMap(({ permissions }) =>
      permissions.map(({ id, codename }) => [id, codename])
    )
  );
  return sections.map((section) => ({
    permissions: section.permissions.map((permission) => ({
      id: permission.codename,
      depends_on: codenameById.get(permission.parent_permission_id),
    })),
  }));
};

const COMPANY_ADMIN_KEY = 'admin';

// A saved change is addressed by key: `perm:<codename>` for a direct
// permission (nested under its panel section), `group:<id>` for a group or
// `admin` for the administrator flag.
export const toPermissionsChangeEntries = ({
  perms = {},
  groups = {},
  isCompanyAdmin,
}) => [
  ...Object.entries(perms).map(([codename, enabled]) => [
    `perm:${codename}`,
    enabled,
  ]),
  ...Object.entries(groups).map(([id, assigned]) => [`group:${id}`, assigned]),
  ...(isCompanyAdmin !== undefined
    ? [[COMPANY_ADMIN_KEY, isCompanyAdmin]]
    : []),
];

const findPanelSlot = (panel, key) => {
  if (key === COMPANY_ADMIN_KEY) {
    return { target: panel, field: 'is_company_admin' };
  }
  const separator = key.indexOf(':');
  const type = key.slice(0, separator);
  const id = key.slice(separator + 1);

  if (type === 'group') {
    const group = panel.groups?.find((g) => String(g.id) === id);
    return group && { target: group, field: 'assigned' };
  }
  const section = Object.values(panel.perms ?? {}).find((s) => id in s);
  return section && { target: section, field: id };
};

export const getPanelValue = (panel, key) => {
  const slot = findPanelSlot(panel, key);
  return slot?.target[slot.field];
};

export const setPanelValue = (panel, key, value) => {
  const slot = findPanelSlot(panel, key);
  if (slot) slot.target[slot.field] = value;
};
